import React from "react";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { fireStore } from "../../firebaseConfig";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const ReportsShow = () => {
  const { id } = useParams(); // Get the appraisal ID from the URL
  const [appraisal, setAppraisal] = useState(null);

  useEffect(() => {
    const fetchAppraisal = async () => {
      try {
        const docRef = doc(fireStore, "Appraisals", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setAppraisal(docSnap.data());
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error("Error fetching appraisal:", error);
      }
    };

    if (id) {
      fetchAppraisal();
    }
  }, [id]);

  if (!appraisal) {
    return <div>Loading Reports</div>; // Fallback if no appraisal is found
  }

  const exportToPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Temporarily show the element if it's hidden
    const ReportsContainer = document.getElementById("ReportsContainer");
    const originalDisplay = ReportsContainer.style.display;
    ReportsContainer.style.display = "block";
    await new Promise((resolve) => setTimeout(resolve, 100)); // Allow layout to update

    // Capture the entire content of the ReportsContainer
    const canvas = await html2canvas(ReportsContainer, { scale: 1 });
    const imgData = canvas.toDataURL("image/jpeg", 0.7);
    const imgWidth = pageWidth - 2 * margin;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Split content across multiple pages if needed
    if (imgHeight > pageHeight - 2 * margin) {
      const numPages = Math.ceil(imgHeight / (pageHeight - 2 * margin));
      let startY = 0;

      for (let i = 0; i < numPages; i++) {
        if (i > 0) {
          doc.addPage();
          yPosition = margin;
        }

        const sliceHeight = pageHeight - 2 * margin;
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = imgWidth * 2;
        sliceCanvas.height = sliceHeight * 2;
        const sliceCtx = sliceCanvas.getContext("2d");
        const sliceImg = new Image();
        sliceImg.src = imgData;

        await new Promise((resolve) => {
          sliceImg.onload = () => {
            sliceCtx.drawImage(sliceImg, 0, -startY, imgWidth * 2, imgHeight);
            const sliceImgData = sliceCanvas.toDataURL("image/png");
            doc.addImage(
              sliceImgData,
              "PNG",
              margin,
              yPosition,
              imgWidth,
              sliceHeight
            );
            startY += sliceHeight;
            resolve();
          };
        });
      }
    } else {
      doc.addImage(imgData, "PNG", margin, yPosition, imgWidth, imgHeight);
    }

    doc.save("Reports.pdf");

    // Restore original display style
    ReportsContainer.style.display = originalDisplay;
  };

  return (
    <div className="p-8 bg-lightgray h-full overflow-y-scroll">
      <div className="flex flex-col md:flex-row justify-between items-center">
        <h2 className="text-2xl font-semibold mb-4 md:mb-0 font-lato">
          Show Report
        </h2>
        <div className=" space-x-4 hidden">
          <div className="flex items-center">
            <label htmlFor="from-date" className="mr-2 font-lato">
              From Date
            </label>
            {/* <input type="date" id="from-date" className="border p-2 rounded" /> */}
            <svg
              width="15"
              height="13"
              viewBox="0 0 15 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.5 13L0.138786 0.249999L14.8612 0.25L7.5 13Z"
                fill="#056886"
              />
            </svg>
          </div>
          <div className="flex items-center">
            <label htmlFor="to-date" className="mr-2 font-lato">
              To Date
            </label>
            {/* <input type="date" id="to-date" className="border p-2 rounded" /> */}
            <svg
              width="15"
              height="13"
              viewBox="0 0 15 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.5 13L0.138786 0.249999L14.8612 0.25L7.5 13Z"
                fill="#056886"
              />
            </svg>
          </div>
        </div>
      </div>
      <div
        id="ReportsContainer"
        className="bg-white p-6 rounded-lg shadow-lg w-full max-w-full mt-4"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white shadow-2xl">
            <thead>
              <tr className=" uppercase text-sm leading-normal">
                <th className="py-3 px-6 text-left font-lato font-bold text-liquidblue font-2xl ">
                  Name
                </th>
                <th className="py-3 px-6 text-center font-lato text-liquidblue font-2xl font-bold">
                  Designation
                </th>
                <th className="py-3 px-6 text-center font-lato text-liquidblue font-2xl font-bold">
                  User Name
                </th>
              </tr>
            </thead>
            <tbody className="text-gray-600 text-sm font-light ">
              <tr className="shadow-xl rounded-lg">
                <td className="py-3 px-6 text-left whitespace-nowrap font-lato font-bold text-black">
                  {appraisal.appraisedName}
                </td>
                <td className="py-3 px-6 text-center font-lato font-bold text-black">
                  {appraisal.designation}
                </td>
                <td className="py-3 px-6 text-center font-lato font-bold text-black">
                  {appraisal.user?.name}
                </td>

                <td className="flex justify-end">
                  <button
                    onClick={exportToPDF}
                    className="border border-darkteal text-darkteal rounded-lg  px-4 py-2"
                  >
                    PDF
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="space-y-6">
          <div>
            <div className="flex justify-between mt-4 py-2">
              <h3 className="text-base font-semibold text-darkteal font-lato">
                Performance report
              </h3>
              <Link
                to="/reports"
                className="bg-darkteal text-white px-4 py-2 rounded-lg font-lato font-bold"
              >
                Close
              </Link>
            </div>

            <p className="text-sm text-gray-600 font-lato">
              {appraisal.performanceRep}
            </p>
          </div>

          <div>
            <h3 className="text-base font-semibold text-darkteal font-lato">
              Behaviour report
            </h3>
            <p className="text-sm text-gray-600 font-lato ">
              {appraisal.behaviourRep}
            </p>
          </div>

          <div>
            <h3 className="text-base font-semibold text-darkteal font-lato">
              Strengths
            </h3>
            <p className="text-sm text-gray-600 font-lato ">
              {appraisal.strengthsRep}
            </p>
          </div>

          <div>
            <h3 className="text-base font-semibold text-darkteal font-lato">
              Weaknesses
            </h3>
            <p className="text-sm text-gray-600 font-lato ">
              {appraisal.weaknessesRep}
            </p>
          </div>

          <div>
            <h3 className="text-base font-semibold text-darkteal font-lato">
              Recommendations
            </h3>
            <p className="text-sm text-gray-600 font-lato ">
              {appraisal.recommendations}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsShow;
